import { createContext, useContext } from "react"
import { shallowEqual } from "react-redux"

import { useAppSelector } from "@/App"
import { ContextMenu, createContextMenu } from "@/components/ContextMenu"
import { selectDriverById } from "@/store/drivers"
import { Driver } from "@/types"

import { DriversBreakDeleteModal } from "./partials/DriversBreakDeleteModal"
import { DriversBreakModal } from "./partials/DriversBreakModal"
import { DriversFinalBreakDeleteModal } from "./partials/DriversFinalBreakDeleteModal"
import { DriversFinalBreakModal } from "./partials/DriversFinalBreakModal"
import { DriversPlanShiftsModal } from "./partials/DriversPlanShiftsModal"

export type DriverContextMenuAction = {
  action: "add_breaks" | "add_shift_end" | "manage_shifts" | "delete_breaks" | "delete_shift_end"
  driverId: Driver["id"]
} | null

export const DriverItemContext = createContext<{
  driverContextMenuAction: DriverContextMenuAction
  setDriverContextMenuAction: (action: DriverContextMenuAction) => void
} | null>(null)

export const {
  ContextMenuWrapper: DriversContextMenuWrapper,
  useContextMenuEvent: useDriversContextMenuEvent,
  useContextMenuTrigger: useDriversContextMenuTrigger,
} = createContextMenu<{
  driverId: Driver["id"]
}>("driversList")

export const DriverContextMenu = () => {
  return (
    <>
      <DriversContextMenuWrapper>
        <ContextMenuItems />
      </DriversContextMenuWrapper>

      <ContextMenuActions />
    </>
  )
}

const ContextMenuItems = () => {
  const { setDriverContextMenuAction } = useContext(DriverItemContext) || {}

  const {
    data: { driverId },
  } = useDriversContextMenuEvent()

  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)

  if (!setDriverContextMenuAction) return null

  return (
    <ContextMenu>
      <ContextMenu.Item
        iconName="stoper"
        onClick={() => setDriverContextMenuAction({ action: "add_breaks", driverId })}
      >
        Dodaj przerwę
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="home"
        onClick={() => setDriverContextMenuAction({ action: "add_shift_end", driverId })}
      >
        Dodaj koniec zmiany
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="today"
        onClick={() => setDriverContextMenuAction({ action: "manage_shifts", driverId })}
      >
        Zaplanuj zmiany
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="cash"
        onClick={() =>
          window.open(
            `https://app.deligoo.pl/admin/deductions/new?team_id=${  driver?.team_id  }&driver_id=${  driverId}`,
            "_blank"
          )
        }
      >
        Dodaj potrącenie
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="clock-alt"
        disabled={driver?.today_breaks.length === 0}
        onClick={() => setDriverContextMenuAction({ action: "delete_breaks", driverId })}
      >
        Usuń przerwę
      </ContextMenu.Item>

      <ContextMenu.Item
        iconName="clock-alt"
        disabled={driver?.today_temporary_break === null}
        onClick={() => setDriverContextMenuAction({ action: "delete_shift_end", driverId })}
      >
        Usuń koniec zmiany
      </ContextMenu.Item>
    </ContextMenu>
  )
}

const ContextMenuActions = () => {
  const { driverContextMenuAction, setDriverContextMenuAction } = useContext(DriverItemContext) || {}

  if (!driverContextMenuAction || !setDriverContextMenuAction) return null

  const { action, driverId } = driverContextMenuAction

  switch (action) {
    case "add_breaks":
      return <DriversBreakModal driverId={driverId} handleClose={() => setDriverContextMenuAction(null)} />

    case "add_shift_end":
      return <DriversFinalBreakModal driverId={driverId} handleClose={() => setDriverContextMenuAction(null)} />

    case "manage_shifts":
      return <DriversPlanShiftsModal driverId={driverId} handleClose={() => setDriverContextMenuAction(null)} />

    case "delete_breaks":
      return <DriversBreakDeleteModal driverId={driverId} handleClose={() => setDriverContextMenuAction(null)} />

    case "delete_shift_end":
      return <DriversFinalBreakDeleteModal driverId={driverId} handleClose={() => setDriverContextMenuAction(null)} />
  }
}
